import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, LogOut, Maximize, MapPin, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const COUNTERS = [
  { key: "active", label: "With you now", cls: "text-[#005EB8]" },
  { key: "high", label: "High priority", cls: "text-[#D5281B]", alert: true },
  { key: "discharged", label: "Discharged", cls: "text-slate-500" },
  { key: "treated", label: "Treated total", cls: "text-[#007F3B]" },
  { key: "lost_with_us", label: "Lost children", cls: "text-[#ED8B00]", alert: true },
];

const fmtDay = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";

export default function ControlOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clock, setClock] = useState(new Date());
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/control/overview");
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/control-login");
      } else {
        toast.error(formatApiError(err));
      }
    }
  }, [navigate]);

  useEffect(() => {
    api.get("/control/status").then((r) => {
      if (!r.data.authenticated) navigate("/control-login");
    }).catch(() => navigate("/control-login"));
    load();
    timer.current = setInterval(load, 8000);
    const c = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(timer.current); clearInterval(c); };
  }, [load, navigate]);

  const logout = async () => {
    await api.post("/control/logout").catch(() => {});
    navigate("/control-login");
  };

  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  const events = data?.events || [];

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="control-overview-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-xl bg-[#F4F7FA] border border-slate-200 grid place-items-center">
            <Radio className="h-5 w-5 text-[#005EB8]" />
          </div>
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]" data-testid="control-overview-title">Control Room</h1>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#007F3B]" /> Live — all events
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-500 tabular-nums hidden sm:inline" data-testid="control-clock">
              {clock.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}{" "}
              {clock.toLocaleTimeString("en-GB", { hour12: false })}
            </span>
            <button onClick={fullscreen} data-testid="control-fullscreen-btn" title="Fullscreen"
              className="h-10 w-10 rounded-lg border border-slate-300 text-slate-500 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors grid place-items-center bg-white">
              <Maximize className="h-4 w-4" />
            </button>
            <button onClick={logout} data-testid="control-logout-btn" title="Sign out of control room"
              className="h-10 w-10 rounded-lg border border-slate-300 text-slate-500 hover:text-[#D5281B] hover:border-[#D5281B] transition-colors grid place-items-center bg-white">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-6">
        {data === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="control-no-events">
            <p className="text-slate-600 text-sm font-semibold">No active events right now.</p>
            <p className="text-slate-400 text-xs mt-1">Events added to the calendar appear here automatically.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {events.map((ev) => (
              <button key={ev.id} onClick={() => navigate(`/control/event/${ev.id}`)}
                data-testid={`event-tile-card-${ev.id}`}
                className={`text-left bg-white rounded-2xl border p-5 transition-all hover:border-[#005EB8] hover:shadow-md ${ev.counts.high > 0 || ev.counts.lost_with_us > 0 ? "border-[#D5281B]/50" : "border-slate-200"}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h2 className="font-heading text-lg font-bold text-[#0F2547] truncate">{ev.name}</h2>
                  <span className="h-2 w-2 rounded-full bg-[#007F3B] shrink-0 mt-2" />
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                  <MapPin className="h-3 w-3 text-[#005EB8]" /> {ev.venue || "Venue TBC"}
                </p>
                <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mb-4">
                  <CalendarDays className="h-3 w-3" />
                  {ev.event_date ? fmtDay(ev.event_date) : "No date"}
                  {ev.end_date && ev.end_date > ev.event_date ? ` → ${fmtDay(ev.end_date)}` : ""}
                  {ev.times ? ` · ${ev.times}` : ""}
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {COUNTERS.map((c) => (
                    <div key={c.key} className="text-center">
                      <p className={`text-2xl font-black tabular-nums ${c.cls}`} data-testid={`tile-${c.key}-${ev.id}`}>
                        {ev.counts[c.key]}
                      </p>
                      <p className="text-[9px] uppercase tracking-wider text-slate-400 leading-tight mt-1">{c.label}</p>
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
